import {
  Injectable,
  Injector,
  NgZone,
  OnDestroy,
  RendererFactory2,
} from '@angular/core';

import { Actions, createEffect, ofType } from '@ngrx/effects';
import { Store } from '@ngrx/store';
import { Node } from '@alfresco/js-api';
import { FileUtils } from '@alfresco/adf-core';
import {
  UploadFilesEvent,
  UploadService,
  FileModel,
} from '@alfresco/adf-content-services';
import {
  UnlockWriteAction,
  UploadActionTypes,
  UploadFilesAction,
  UploadFileVersionAction,
  UploadFolderAction,
} from '@alfresco/aca-shared/store';
import { merge, noop, of, Subject } from 'rxjs';
import {
  catchError,
  map,
  switchMap,
  take,
  takeUntil,
  takeWhile,
  tap,
} from 'rxjs/operators';

import { ContentManagementService } from '@adw/projects/aca-content/src/lib/services/content-management.service';
import { CustomContentManagementService } from '../../services/custom-content-management.service';
import { WKFileUploadService } from '../../services/file-upload.service';
import { WkFileUploadEventService } from '../../services/wk-file-upload-event.service';
import { WkTreeViewService } from '../../services/wk-tree-view.service';

import {
  ON_PAGINATION_CHANGE,
  SetPaginationChange,
  SetPaginationData,
  SetUploadCompletedAction,
  SnackbarErrorAction,
} from '../actions/app.custom-actions';
import { getCurrentFolder } from '../selectors/app.custom-selectors';
import { AppStore } from '../states/app.state';

@Injectable()
export class UploadEffects implements OnDestroy {
  private readonly fileInput: HTMLInputElement;
  private readonly folderInput: HTMLInputElement;
  private readonly fileVersionInput: HTMLInputElement;
  private readonly customContentService: CustomContentManagementService;
  private readonly fileUploadService: WKFileUploadService;
  private readonly fileUploadEventService: WkFileUploadEventService;
  private readonly treeViewService: WkTreeViewService;
  protected readonly onDestroy$ = new Subject<void>();
  docType = null;
  mediaType = '';
  isZipFile = false;

  constructor(
    private readonly store: Store<AppStore>,
    private readonly actions$: Actions,
    private readonly ngZone: NgZone,
    private readonly uploadService: UploadService,

    rendererFactory: RendererFactory2,
    private readonly contentService: ContentManagementService,
    private readonly injector: Injector
  ) {
    this.customContentService =
      this.injector.get<CustomContentManagementService>(
        CustomContentManagementService
      );
    this.fileUploadService =
      this.injector.get<WKFileUploadService>(WKFileUploadService);
    this.fileUploadEventService = this.injector.get<WkFileUploadEventService>(
      WkFileUploadEventService
    );
    this.treeViewService =
      this.injector.get<WkTreeViewService>(WkTreeViewService);

    const renderer = rendererFactory.createRenderer(null, null);

    this.fileInput = renderer.createElement('input') as HTMLInputElement;
    this.fileInput.id = 'app-upload-files';
    this.fileInput.type = 'file';
    this.fileInput.style.display = 'none';
    this.fileInput.setAttribute('multiple', '');
    this.fileInput.addEventListener('change', (event) => {
      this.upload(event);
    });
    renderer.appendChild(document.body, this.fileInput);

    this.fileVersionInput = renderer.createElement('input') as HTMLInputElement;
    this.fileVersionInput.id = 'app-upload-file-version';
    this.fileVersionInput.type = 'file';
    this.fileVersionInput.style.display = 'none';
    this.fileVersionInput.addEventListener('change', () => {
      this.uploadVersion();
    });
    renderer.appendChild(document.body, this.fileVersionInput);

    this.folderInput = renderer.createElement('input') as HTMLInputElement;
    this.folderInput.id = 'app-upload-folder';
    this.folderInput.type = 'file';
    this.folderInput.style.display = 'none';
    this.folderInput.setAttribute('directory', '');
    this.folderInput.setAttribute('webkitdirectory', '');
    this.folderInput.addEventListener('change', (event) => {
      const input = event.currentTarget as HTMLInputElement;
      if (input?.files) {
        this.upload(event);
      }
    });
    renderer.appendChild(document.body, this.folderInput);

    this.fileUploadEventService
      .onFilesUploadEvent()
      .pipe(takeUntil(this.onDestroy$))
      .subscribe((event) => this.upload(event));
    this.uploadCompleted();
  }

  public ngOnDestroy(): void {
    this.onDestroy$.next();
    this.onDestroy$.complete();
  }

  private uploadCompleted(): void {
    merge(
      this.fileUploadService.fileUploadComplete,
      this.fileUploadService.fileUploadDeleted
    )
      .pipe(takeUntil(this.onDestroy$))
      .subscribe(() => {
        this.store.dispatch(new SetUploadCompletedAction(true));
      });
  }

  uploadFiles$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType<UploadFilesAction>(UploadActionTypes.UploadFiles),
        map((action) => {
          if (action?.payload) {
            if (action.payload === 'application/zip') {
              this.fileInput.accept = action.payload;
              this.fileInput.multiple = false;
              this.isZipFile = true;
            } else {
              this.fileInput.accept = action.payload.accept;
              this.docType = action.payload.type;
              this.mediaType = action.payload.mediaType;
            }
          }
          action?.payload !== null
            ? this.fileInput.click()
            : this.resetInput(null);
        })
      ),
    { dispatch: false }
  );

  uploadFolder$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType<UploadFolderAction>(UploadActionTypes.UploadFolder),
        map(() => {
          this.folderInput.click();
        })
      ),
    { dispatch: false }
  );

  uploadVersion$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType<UploadFileVersionAction>(UploadActionTypes.UploadFileVersion),
        map((action) => {
          if (action?.payload) {
            const node = action.payload.detail.data.node.entry;
            const file: any = action.payload.detail.files[0].file;
            if (
              (node.content.mimeType !== file.type && file.type) ||
              node.name.split('.').pop() !== file.name.split('.').pop()
            ) {
              this.store.dispatch(
                new SnackbarErrorAction('VERSION.ERROR.FILETYPE_MISMATCH')
              );
            } else {
              this.customContentService
                .versionUpdateDialog(node, file)
                .afterClosed()
                .subscribe((result) => {
                  if (result) {
                    this.customContentService.versionHistory$.next(true);
                  }
                });
            }
          } else if (!action.payload) {
            this.fileVersionInput.click();
          }
        })
      ),
    { dispatch: false }
  );

  getSpecificFiles$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType<SetPaginationChange>(ON_PAGINATION_CHANGE),
        map((action) => {
          if (action?.payload) {
            this.treeViewService
              .getSpecificFiles(
                action.payload.mediaType,
                action.payload.searchField,
                action.payload.currentSite,
                action.payload.paging
              )
              .subscribe((files) => {
                this.store.dispatch(new SetPaginationData(files));
              });
          }
        })
      ),
    { dispatch: false }
  );

  uploadVersion() {
    this.contentService
      .getNodeInfo()
      .pipe(
        catchError((_) => {
          this.store.dispatch(new SnackbarErrorAction('VERSION.ERROR.GENERIC'));
          return of(null);
        })
      )
      .subscribe((node: Node) => {
        if (node) {
          if (
            (node.content.mimeType !== this.fileVersionInput.files[0].type &&
              this.fileVersionInput.files[0].type) ||
            node.name.split('.').pop() !==
              this.fileVersionInput.files[0].name.split('.').pop()
          ) {
            this.store.dispatch(
              new SnackbarErrorAction('VERSION.ERROR.FILETYPE_MISMATCH')
            );
          } else {
            this.customContentService
              .versionUpdateDialog(node, this.fileVersionInput.files[0])
              .afterClosed()
              .subscribe((result) => {
                if (result) {
                  this.customContentService.versionHistory$.next(true);
                }
              });
          }
          this.fileVersionInput.value = '';
        }
      });
  }

  private getFileModels(event: Event | UploadFilesEvent): FileModel[] {
    let fileModels: FileModel[];
    if (event instanceof UploadFilesEvent) {
      event.pauseUpload();
      event.files.forEach(
        (fileModel) => (fileModel.options.properties = Object.create(null))
      );
      fileModels = event.files;
    } else {
      const files = (event.currentTarget as HTMLInputElement).files;
      fileModels = FileUtils.toFileArray(files).map(
        (file: any) =>
          new FileModel(file, {
            path: (file.webkitRelativePath || '').replace(/\/[^\/]*$/, ''),
            properties: Object.create(null),
          })
      );
    }
    return fileModels;
  }

  public upload(event: Event | UploadFilesEvent): void {
    const fileModelArray = this.getFileModels(event);
    if (this.isZipFile) {
      this.fileUploadService.uploadZipFile(fileModelArray);
    } else {
      if (this.docType !== null) {
        const result = this.checkMediaFile(fileModelArray);
        if (!result) {
          this.store.dispatch(new SnackbarErrorAction(this.getErrorMessage()));
          return;
        }
      }
      this.fileUploadService
        .showUploadDialog(fileModelArray, null, null, this.docType)
        .subscribe((uploadConfig) => {
          if (uploadConfig.duplicate) {
            this.fileUploadService.showDuplicateDialog(
              uploadConfig.duplicate,
              fileModelArray.length !== 1
            );
          }
          if (uploadConfig.uploadQueue) {
            this.listenForFolderUpload(fileModelArray[0]);
            this.uploadQueue(fileModelArray);
          }
        });
    }
    this.resetInput(event);
  }

  private listenForFolderUpload({ options: { parentId, path } }: FileModel) {
    if (path) {
      this.fileUploadService.fileUploadComplete
        .pipe(
          take(1),
          switchMap(() =>
            this.store.select(getCurrentFolder).pipe(
              takeWhile(({ id }: Node) => id === parentId),
              tap(({ id }: Node) => {
                this.treeViewService.triggerTreeViewReload({
                  type: 'CREATE',
                  parentFolderId: id,
                });
              }),
              take(1)
            )
          )
        )
        .subscribe(noop);
    }
  }

  resetInput(event: Event | UploadFilesEvent | null) {
    if (event && !(event instanceof UploadFilesEvent)) {
      (event.target as HTMLInputElement).value = '';
    }
    this.fileInput.accept = '*';
    this.fileInput.multiple = true;
    this.docType = null;
    this.mediaType = '';
    this.isZipFile = false;
  }

  checkMediaFile(fileModels: Array<FileModel>) {
    return fileModels.every((fileModel) => {
      const mimeType = fileModel.file.type;
      const type = this.mediaType.split(',')[0];
      return mimeType.startsWith(type) || this.mediaType.includes(mimeType);
    });
  }

  private uploadQueue(files: FileModel[]): void {
    if (files.length > 0) {
      this.ngZone.run(() => {
        this.uploadService.addToQueue(...files);
        this.uploadService.uploadFilesInTheQueue();
      });
    }
  }

  uploadAndUnlock(file: FileModel) {
    if (!file) {
      return;
    }

    this.ngZone.run(() => {
      this.uploadService.addToQueue(file);
      this.uploadService.uploadFilesInTheQueue();

      const subscription = this.uploadService.fileUploadComplete.subscribe(
        (completed) => {
          if (
            file.data?.entry?.properties?.['cm:lockType'] === 'WRITE_LOCK' &&
            file.data.entry.id === completed.data.entry.id
          ) {
            this.store.dispatch(new UnlockWriteAction(completed.data));
          }
          subscription.unsubscribe();
        }
      );
    });
  }

  getErrorMessage() {
    const primaryMediaType = this.mediaType.split(',')[0];
    if (primaryMediaType === 'image') {
      return 'APP.MEDIA.UPLOAD_IMAGE_FILE_ERROR';
    } else if (primaryMediaType === 'audio') {
      return 'APP.MEDIA.UPLOAD_AUDIO_FILE_ERROR';
    } else {
      return 'APP.MEDIA.UPLOAD_VIDEO_FILE_ERROR';
    }
  }
}
